import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { intl } from "LocaleWrapper";
import { useLazyQuery, useQuery  } from "@apollo/client";
import { useStoreState } from "ezStore";

import { FormGroup, Checkbox, MarkerProps, EncodedPolyLine } from "@cofano/cck";
import usePropertyChangeEffect from "@cofano/cck/hooks/usePropertyChangeEffect";
import { Box } from "@mui/material";

import LocationSelect, { LocationSelectOption } from "view/locations/components/selects/LocationSelect";
import LocationTypeSelect from "view/locations/components/selects/LocationTypeSelect";
import { getLocationTypeNumber } from "view/locations/utils/LocationUtils";

import { DataFormat, Fields, hasOptionalFieldPermission, Language } from "imports/Utils";

import { ChildPropsFromPagination } from "types/commons";

import LocationMarker from "../markers/LocationMarker";

import { LocationType } from "__generated__/enums";
import { GetLocationOnMapDocument, SearchOnLocationsOnMapDocument, SearchOnLocationsOnMapQuery } from "__generated__/view/locations/graphql/location.queries.generated";

type LocationFromQuery = ChildPropsFromPagination<SearchOnLocationsOnMapQuery, "searchLocations">

const MapLocationsTab = ({ onChange }: {
	onChange: (markers: MarkerProps[], encodedPolylines: EncodedPolyLine[]) => void;
}) => {
	const [ searchParams, setSearchParams ] = useSearchParams();

	const [ types, setTypes ] = useState<LocationType[] | null>(() => {
		const param = searchParams.get("types");
		return param ? JSON.parse(param) : null;
	});

	const [ location, setLocation ] = useState<LocationSelectOption | null>(() => {
		const param = searchParams.get("location");
		return param ? JSON.parse(param) : null;
	});

	const [ showGeofences, toggleGeofences ] = useState<boolean>(() => {
		const param = searchParams.get("showGeofences");
		return param ? JSON.parse(param) : false;
	});

	const [ locations, setLocations ] = useState<LocationFromQuery[]>([]);

	const [ getLocations, { loading } ] = useLazyQuery(SearchOnLocationsOnMapDocument);

	const getPolylineOptions = (encodedPolyline: string)	 => {
		return {
			color: "red",
			line: encodedPolyline,
			fillColor: "red",
			fillOpacity: 0.6,
		};
	};

	const createMarkers = (locations: LocationFromQuery[]) => {

		const markers: MarkerProps[] = [];
		const encodedPolylines: EncodedPolyLine[] = [];

		locations.forEach(location => {
			if (location.position?.latitude && location.position.longitude) {
				markers.push({
					lat: location.position.latitude,
					lng: location.position.longitude,
					label: location.description,
					component: () => (
						<LocationMarker
							location={location}
							onClick={() => {
								setLocation(location);
								createMarkers([ { ...location } ]);
							}} />
					),
				});

				if (location.encodedPolyline && showGeofences) {
					encodedPolylines.push(getPolylineOptions(location.encodedPolyline));
				}
			}
		});

		onChange(markers, encodedPolylines);
	};

	const clearMarkers = () => {
		onChange([], []);
	};

	const retrieveLocations = (currentOptions: LocationType[]) => {
		getLocations({
			variables: {
				locationSearch: {
					types: currentOptions.map((type) => getLocationTypeNumber(type)),
				},
				pagination: null,
			},
		}).then((response) => {
			setLocations(response.data?.searchLocations.content || []);
			createMarkers(response.data?.searchLocations.content || []);
		});
	};

	usePropertyChangeEffect(() => {
		const findLocation = locations.find(it => it.id === location?.id);

		if (findLocation) {
			createMarkers([ { ...findLocation } ]);
		} else if (types) {
			retrieveLocations(types);
		}
	}, [ showGeofences ]);

	useEffect(() => {
		setSearchParams((prev) => {
			const latestParams = new URLSearchParams(prev);

			if (types && types.length > 0) {
				latestParams.set("types", JSON.stringify(types));
			} else {
				latestParams.delete("types");
			}

			if (location) {
				latestParams.set("location", JSON.stringify(location));
			} else {
				latestParams.delete("location");
			}

			if (showGeofences) {
				latestParams.set("showGeofences", JSON.stringify(showGeofences));
			} else {
				latestParams.delete("showGeofences");
			}

			return latestParams;
		}, {
			replace: true,
		});
	}, [ types, location, showGeofences ]);

	useEffect(() => {
		if (types) {
			retrieveLocations(types);
		}
	}, []);

	return (
		<Box p={2}>
			<FormGroup label={intl.formatMessage({ id: "SELECT_LOCATION_TYPE" })}>
				<LocationTypeSelect
					value={types}
					multiple={true}
					onChange={(op) => {
						setTypes(op);

						if (op) {
							retrieveLocations(op);
						} else {
							setLocation(null);
							clearMarkers();
						}
					}} />
			</FormGroup>

			<FormGroup label={intl.formatMessage({ id: "SELECT_LOCATION" })}>
				<LocationSelect
					value={location}
					types={types ?? undefined}
					disabled={loading}
					onChange={(op) => {
						setLocation(op);

						const findLocation = locations.find(it => it.id === op?.id);

						if (op && findLocation) {
							createMarkers([ { ...findLocation } ]);
						} else {
							createMarkers(locations);
						}
					}}
				/>
			</FormGroup>

			<FormGroup label={intl.formatMessage({ id: "SHOW_GEOFENCES" })} labelPlacement="left-center">
				<Checkbox
					checked={showGeofences}
					onChange={(e) => toggleGeofences(e.target.checked)}
				/>
			</FormGroup>

			<br />

			{location && <LocationInformation locationId={location.id} />}
		</Box>
	);
};

const LocationInformation = ({ locationId }: { locationId: number }) => {

	const session = useStoreState(state => state.session);

	const { data } = useQuery(GetLocationOnMapDocument, {
		variables: {
			id: locationId,
		},
	});

	return (
		<React.Fragment>
			{hasOptionalFieldPermission(session, Fields.LOCATION, "code") && (
				<FormGroup label={Language("CODE")} labelPlacement={"left-center"}>
					{data?.location?.code || "-"}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.LOCATION, "abbreviation") && (
				<FormGroup label={Language("ABBREVIATION")} labelPlacement={"left-center"}>
					{data?.location?.abbreviation || "-"}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.LOCATION, "generalEmail") && (
				<FormGroup label={Language("LOCATION_GENERAL_EMAIL")} labelPlacement={"left-center"}>
					{data?.location?.email || "-"}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.LOCATION, "telephone") && (
				<FormGroup label={Language("LOCATION_TELEPHONE")} labelPlacement={"left-center"}>
					{data?.location?.telephone || "-"}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.LOCATION, "address") && (
				<React.Fragment>
					{hasOptionalFieldPermission(session, Fields.ADDRESS, "name1") && (
						<FormGroup label={Language("CLIENT_NAME_ONE")} labelPlacement={"left-center"}>
							{data?.location?.address?.name1 || "-"}
						</FormGroup>
					)}

					{hasOptionalFieldPermission(session, Fields.ADDRESS, "name2") && (
						<FormGroup label={Language("CLIENT_NAME_TWO")} labelPlacement={"left-center"}>
							{data?.location?.address?.name2 || "-"}
						</FormGroup>
					)}

					{hasOptionalFieldPermission(session, Fields.ADDRESS, "street") && (
						<FormGroup label={Language("CLIENT_STREET")} labelPlacement={"left-center"}>
							{data?.location?.address?.street || "-"}
						</FormGroup>
					)}

					{hasOptionalFieldPermission(session, Fields.ADDRESS, "zipcode") && (
						<FormGroup label={Language("CLIENT_ZIPCODE")} labelPlacement={"left-center"}>
							{data?.location?.address?.zipCode || "-"}
						</FormGroup>
					)}

					{hasOptionalFieldPermission(session, Fields.ADDRESS, "town") && (
						<FormGroup label={Language("PLACE")} labelPlacement={"left-center"}>
							{data?.location?.address?.town || "-"}
						</FormGroup>
					)}

					{hasOptionalFieldPermission(session, Fields.ADDRESS, "country") && (
						<FormGroup label={Language("CLIENT_COUNTRY")} labelPlacement={"left-center"}>
							{data?.location?.address?.country || "-"}
						</FormGroup>
					)}
				</React.Fragment>
			)}

			{hasOptionalFieldPermission(session, Fields.LOCATION, "PreHandlingTimeMin") && (
				<FormGroup label={Language("LOCATION_PRE_HANDLING_TIME_MIN")} labelPlacement={"left-center"}>
					{DataFormat.minutes(data?.location?.preHandlingTimeMin) || "-"}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.LOCATION, "PostHandlingTimeMin") && (
				<FormGroup label={Language("LOCATION_POST_HANDLING_TIME_MIN")} labelPlacement={"left-center"}>
					{DataFormat.minutes(data?.location?.postHandlingTimeMin) || "-"}
				</FormGroup>
			)}
		</React.Fragment>
	);
};

export default MapLocationsTab;
